const STORAGE_PREFIX = "seiva_onboarding_tour"
const ROUTE_SETTLE_MS = 260
const TARGET_RETRY_MS = 80
const TARGET_RETRIES = 5

function storageKeyFor(userId, workspaceId, tourId) {
  return `${STORAGE_PREFIX}:${userId || "anonymous"}:${workspaceId || "workspace"}:${tourId || "global"}`
}

function parseJson(value, fallback) {
  if (!value) return fallback
  try {
    return JSON.parse(value)
  } catch (_error) {
    return fallback
  }
}

function sameRoute(route) {
  if (!route) return true
  const wanted = new URL(route, window.location.origin)
  return wanted.pathname === window.location.pathname && wanted.search === window.location.search
}

function roleAllowed(step, role) {
  if (!step.roles || step.roles.length === 0) return true
  return step.roles.includes(role)
}

function accessAllowed(item, accessMap) {
  const flags = accessFlagsFor(item)
  if (flags.length === 0) return true
  if (!accessMap || Object.keys(accessMap).length === 0) return true
  return flags.some((flag) => accessMap[flag] === true)
}

function accessFlagsFor(item) {
  if (item.access) return Array.isArray(item.access) ? item.access : [item.access]
  return routeAccessFlags(item.route)
}

function routeAccessFlags(route) {
  if (!route) return []

  const url = new URL(route, window.location.origin)
  const path = normalizeRoutePath(url.pathname)

  if (path === "/cockpit" || path.startsWith("/cockpit/")) return ["can_access_cockpit"]
  if (path === "/apps" || path.startsWith("/apps/")) return ["can_access_apps"]
  if (path === "/agents" || path.startsWith("/agents/")) return ["can_access_agents"]
  if (path === "/tools" || path.startsWith("/tools/")) return ["can_access_tools"]
  if (path === "/skills" || path.startsWith("/skills/")) return ["can_access_skills"]
  if (path === "/automations" || path.startsWith("/automations/")) return ["can_access_automations"]
  if (path === "/data" || path.startsWith("/data/")) {
    return ["can_access_data_lake", "can_access_data_models", "can_access_storage"]
  }
  if (path === "/storage" || path.startsWith("/storage/")) return ["can_access_storage"]
  if (path === "/credentials" || path.startsWith("/credentials/")) return ["can_access_connections"]
  if (path === "/api-keys" || path.startsWith("/api-keys/")) return ["can_access_api_keys"]
  if (path === "/logs" || path.startsWith("/logs/")) return ["can_access_audit_logs"]
  if (path === "/subspaces" || path.startsWith("/subspaces/")) return ["can_access_subspaces"]
  if (path === "/dashboard" || path.startsWith("/dashboard/")) return ["can_access_cockpit"]

  return []
}

function normalizeRoutePath(path) {
  if (path === "/embed") return "/cockpit"
  if (path.startsWith("/embed/")) return path.replace(/^\/embed/, "")
  return path
}

function routeForClick(route) {
  if (!route) return null
  const url = new URL(route, window.location.origin)
  return `${url.pathname}${url.search}`
}

function iconMarkup() {
  return `
    <svg class="seiva-onboarding-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4.75v14.5M4.75 12h14.5M7.5 7.5l9 9M16.5 7.5l-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    </svg>
  `
}

const OnboardingTour = {
  mounted() {
    this.tours = parseJson(this.el.dataset.tours, {})
    this.steps = parseJson(this.el.dataset.steps, [])
    this.tours.global = this.tours.global || this.steps
    this.actions = parseJson(this.el.dataset.actions, [])
    this.accessMap = parseJson(this.el.dataset.accessMap, {})
    this.userId = this.el.dataset.userId || "anonymous"
    this.workspaceId = this.el.dataset.workspaceId || "workspace"
    this.role = this.el.dataset.workspaceRole || "member"
    this.activeTourId = "global"
    this.setStorageKeys(this.activeTourId)
    this.index = Number(window.localStorage.getItem(this.pendingKey) || "0")
    this.active = false
    this.targetRetries = 0

    this.createLayer()
    this.boundStart = (event) => this.start(true, event.detail?.tour || "global")
    this.boundPop = () => this.resumeAfterNavigation()
    this.boundPageStop = () => this.resumeAfterNavigation()
    this.boundResize = () => this.active && this.renderCurrent()
    this.boundKeydown = (event) => this.onKeydown(event)

    window.addEventListener("seiva:onboarding:start", this.boundStart)
    window.addEventListener("popstate", this.boundPop)
    window.addEventListener("phx:page-loading-stop", this.boundPageStop)
    window.addEventListener("resize", this.boundResize)
    document.addEventListener("keydown", this.boundKeydown)

    const status = window.localStorage.getItem(this.storageKey)
    if (!status && this.currentSteps().length > 0) {
      window.setTimeout(() => this.start(false, "global"), 700)
    } else if (window.localStorage.getItem(this.pendingKey)) {
      window.setTimeout(() => this.start(false, "global"), ROUTE_SETTLE_MS)
    }
  },

  destroyed() {
    window.removeEventListener("seiva:onboarding:start", this.boundStart)
    window.removeEventListener("popstate", this.boundPop)
    window.removeEventListener("phx:page-loading-stop", this.boundPageStop)
    window.removeEventListener("resize", this.boundResize)
    document.removeEventListener("keydown", this.boundKeydown)
    this.layer?.remove()
  },

  createLayer() {
    this.layer = document.createElement("div")
    this.layer.className = "seiva-onboarding-layer hidden"
    this.layer.setAttribute("aria-hidden", "true")
    this.layer.innerHTML = `
      <div class="seiva-onboarding-highlight" aria-hidden="true"></div>
      <section class="seiva-onboarding-card" role="dialog" aria-modal="true" aria-labelledby="seiva-onboarding-title">
        <div class="seiva-onboarding-card-header">
          <span class="seiva-onboarding-icon-wrap">${iconMarkup()}</span>
          <div class="seiva-onboarding-title-wrap">
            <p class="seiva-onboarding-progress"></p>
            <h2 id="seiva-onboarding-title"></h2>
          </div>
        </div>
        <p class="seiva-onboarding-body"></p>
        <div class="seiva-onboarding-actions hidden"></div>
        <div class="seiva-onboarding-footer">
          <button type="button" class="seiva-onboarding-skip" data-action="skip"></button>
          <div class="seiva-onboarding-nav">
            <button type="button" class="seiva-onboarding-back" data-action="back"></button>
            <button type="button" class="seiva-onboarding-next" data-action="next"></button>
          </div>
        </div>
      </section>
    `
    document.body.appendChild(this.layer)
    this.highlight = this.layer.querySelector(".seiva-onboarding-highlight")
    this.card = this.layer.querySelector(".seiva-onboarding-card")
    this.titleEl = this.layer.querySelector("#seiva-onboarding-title")
    this.bodyEl = this.layer.querySelector(".seiva-onboarding-body")
    this.progressEl = this.layer.querySelector(".seiva-onboarding-progress")
    this.actionsEl = this.layer.querySelector(".seiva-onboarding-actions")
    this.skipBtn = this.layer.querySelector("[data-action='skip']")
    this.backBtn = this.layer.querySelector("[data-action='back']")
    this.nextBtn = this.layer.querySelector("[data-action='next']")
    this.card.setAttribute("tabindex", "-1")

    this.skipBtn.textContent = this.el.dataset.skipLabel || "Skip"
    this.backBtn.textContent = this.el.dataset.backLabel || "Back"
    this.nextBtn.textContent = this.el.dataset.nextLabel || "Next"

    this.layer.addEventListener("click", (event) => {
      const action = event.target.closest("[data-action]")?.dataset.action
      if (!action) return

      if (action === "skip") this.skip()
      if (action === "back") this.back()
      if (action === "next") this.next()
      if (action?.startsWith("finish:")) this.finishAction(action.replace("finish:", ""))
    })
  },

  setStorageKeys(tourId) {
    this.storageKey = storageKeyFor(this.userId, this.workspaceId, tourId)
    this.pendingKey = `${this.storageKey}:pending`
  },

  start(reset, tourId = "global") {
    this.activeTourId = this.tours[tourId] ? tourId : "global"
    this.setStorageKeys(this.activeTourId)

    if (reset) {
      this.index = 0
      window.localStorage.removeItem(this.storageKey)
      window.localStorage.setItem(this.pendingKey, "0")
    } else {
      this.index = Number(window.localStorage.getItem(this.pendingKey) || "0")
    }

    this.active = true
    this.layer.classList.remove("hidden")
    this.layer.setAttribute("aria-hidden", "false")
    document.body.classList.add("seiva-onboarding-active")
    this.renderCurrent()
  },

  resumeAfterNavigation() {
    if (!this.active && !window.localStorage.getItem(this.pendingKey)) return
    window.setTimeout(() => {
      this.active = true
      this.layer.classList.remove("hidden")
      this.layer.setAttribute("aria-hidden", "false")
      document.body.classList.add("seiva-onboarding-active")
      this.renderCurrent()
    }, ROUTE_SETTLE_MS)
  },

  visibleSteps() {
    return this.currentSteps().filter((step) => (
      roleAllowed(step, this.role) && accessAllowed(step, this.accessMap)
    ))
  },

  currentSteps() {
    return this.tours[this.activeTourId] || []
  },

  stepAt(index) {
    return this.visibleSteps()[index]
  },

  renderCurrent() {
    const steps = this.visibleSteps()
    if (steps.length === 0) return this.complete()
    if (this.index < 0) this.index = 0
    if (this.index >= steps.length) return this.complete()

    const step = steps[this.index]
    window.localStorage.setItem(this.pendingKey, String(this.index))

    if (step.route && !sameRoute(step.route)) {
      return this.navigate(step.route)
    }

    this.openMobileSidebarFor(step)
    const target = this.findTarget(step)
    if (step.anchor && !target) {
      if (this.targetRetries < TARGET_RETRIES && !step.optional) {
        this.targetRetries += 1
        window.setTimeout(() => this.renderCurrent(), TARGET_RETRY_MS)
        return
      }
      this.targetRetries = 0
      return this.next()
    }

    this.targetRetries = 0
    this.renderStep(step, target, steps.length)
  },

  openMobileSidebarFor(step) {
    if (!step.anchor || !(step.anchor === "sidebar-nav" || step.anchor.startsWith("sidebar-"))) return
    if (!window.matchMedia("(max-width: 1023px)").matches) return

    const sidebar = document.getElementById("app-sidebar")
    const backdrop = document.getElementById("sidebar-backdrop")
    if (sidebar) sidebar.classList.add("mobile-open")
    if (backdrop) backdrop.classList.add("visible")
    document.body.classList.add("overflow-hidden")
  },

  findTarget(step) {
    if (!step.anchor) return null
    const target = document.querySelector(`[data-tour="${step.anchor}"]`)
    if (!target) return null
    const rect = target.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return null
    return target
  },

  renderStep(step, target, total) {
    this.titleEl.textContent = step.title || ""
    this.bodyEl.textContent = step.body || ""
    this.progressEl.textContent = `${this.index + 1} / ${total}`
    this.backBtn.disabled = this.index === 0
    this.nextBtn.textContent = this.index === total - 1 ? (this.el.dataset.finishLabel || "Finish") : (this.el.dataset.nextLabel || "Next")

    if (this.index === total - 1) this.renderFinishActions()
    else this.actionsEl.classList.add("hidden")

    this.positionHighlight(target)
    this.positionCard(target)
    window.requestAnimationFrame(() => this.card.focus())
  },

  renderFinishActions() {
    const actions = this.actions.filter((action) => (
      roleAllowed(action, this.role) && accessAllowed(action, this.accessMap)
    ))
    if (actions.length === 0) {
      this.actionsEl.classList.add("hidden")
      return
    }

    this.actionsEl.innerHTML = actions.map((action) => `
      <button type="button" class="seiva-onboarding-action" data-action="finish:${action.id}">
        ${iconMarkup()}
        <span>${action.label}</span>
      </button>
    `).join("")
    this.actionsEl.classList.remove("hidden")
  },

  positionHighlight(target) {
    if (!target) {
      this.layer.classList.add("seiva-onboarding-layer--modal")
      this.highlight.classList.add("hidden")
      return
    }

    this.layer.classList.remove("seiva-onboarding-layer--modal")
    target.scrollIntoView({ block: "center", inline: "center", behavior: this.reducedMotion() ? "auto" : "smooth" })
    const rect = target.getBoundingClientRect()
    const pad = 8
    this.highlight.classList.remove("hidden")
    this.highlight.style.left = `${Math.max(rect.left - pad, 8)}px`
    this.highlight.style.top = `${Math.max(rect.top - pad, 8)}px`
    this.highlight.style.width = `${Math.min(rect.width + pad * 2, window.innerWidth - 16)}px`
    this.highlight.style.height = `${Math.min(rect.height + pad * 2, window.innerHeight - 16)}px`
  },

  positionCard(target) {
    this.card.removeAttribute("style")
    if (window.matchMedia("(max-width: 767px)").matches) return

    const cardRect = this.card.getBoundingClientRect()
    const margin = 18
    let left = Math.round((window.innerWidth - cardRect.width) / 2)
    let top = Math.round((window.innerHeight - cardRect.height) / 2)

    if (target) {
      const rect = target.getBoundingClientRect()
      left = rect.right + margin
      top = rect.top

      if (left + cardRect.width + margin > window.innerWidth) left = rect.left - cardRect.width - margin
      if (left < margin) left = Math.min(window.innerWidth - cardRect.width - margin, margin)
      if (top + cardRect.height + margin > window.innerHeight) top = window.innerHeight - cardRect.height - margin
      if (top < margin) top = margin
    }

    this.card.style.left = `${left}px`
    this.card.style.top = `${top}px`
  },

  navigate(route) {
    const href = routeForClick(route)
    if (!href) return
    const link = document.querySelector(`a[href="${href}"], a[data-phx-link="redirect"][href="${href}"], a[data-phx-link="patch"][href="${href}"]`)
    if (link) link.click()
    else window.location.assign(route)
  },

  next() {
    const total = this.visibleSteps().length
    if (this.index >= total - 1) return this.complete()
    this.index += 1
    this.renderCurrent()
  },

  back() {
    if (this.index <= 0) return
    this.index -= 1
    this.renderCurrent()
  },

  skip() {
    window.localStorage.setItem(this.storageKey, "skipped")
    this.hide()
  },

  complete() {
    window.localStorage.setItem(this.storageKey, "completed")
    this.hide()
  },

  hide() {
    window.localStorage.removeItem(this.pendingKey)
    this.active = false
    this.layer.classList.add("hidden")
    this.layer.classList.remove("seiva-onboarding-layer--modal")
    this.layer.setAttribute("aria-hidden", "true")
    document.body.classList.remove("seiva-onboarding-active")
    document.body.classList.remove("overflow-hidden")
    document.getElementById("app-sidebar")?.classList.remove("mobile-open")
    document.getElementById("sidebar-backdrop")?.classList.remove("visible")
  },

  finishAction(id) {
    const action = this.actions.find((item) => item.id === id)
    this.complete()
    if (!action) return

    if (action.event === "toggle_monica_drawer") {
      document.querySelector('[data-tour="monica-btn"]')?.click()
      return
    }

    if (action.route) this.navigate(action.route)
  },

  onKeydown(event) {
    if (!this.active) return
    if (event.key === "Escape") {
      event.preventDefault()
      this.skip()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.back()
    } else if (event.key === "Tab") {
      this.trapFocus(event)
    }
  },

  trapFocus(event) {
    const focusable = Array.from(this.card.querySelectorAll("button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"))
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  },

  reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  },
}

export default OnboardingTour
